
import React, { useEffect, useState } from "react";
import axios from "axios";
import Header from "./Header";

const API = {
  subcategories:
    "https://api.welbeing.in/admin/categories/subcategories/",
  sections: "https://api.welbeing.in/home/combo/",
  add: "https://api.welbeing.in/home/addcombo/",
  delete: "https://api.welbeing.in/home/delete-combo/",
};

const MAX_SECTIONS = 6;

const getErrorMessage = (err, fallback) =>
  err?.response?.data?.message ||
  err?.message ||
  fallback;

const BannerSection = () => {
  const [bannerSections, setBannerSections] = useState([]);
  const [subcategory, setSubcategory] = useState([]);
  const [subId, setSubId] = useState("");
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState(null);

  const [tag, setTag] = useState("");
  const [label, setLabel] = useState("");
  const [price, setPrice] = useState("");
  const [badge, setBadge] = useState("");

  // Fetch subcategories. Let errors reach the caller.
  const fetchSubCategories = async () => {
    const response = await axios.get(API.subcategories);
    const data = response.data;

    if (
      data?.status !== "success" ||
      !Array.isArray(data.data)
    ) {
      throw new Error(
        data?.message || "Failed to fetch subcategories."
      );
    }

    setSubcategory(data.data);
  };

  // Fetch sections. Clear errors only after both initial requests succeed.
  const fetchBannerSections = async () => {
    const response = await fetch(API.sections);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch budget sections (HTTP ${response.status}).`
      );
    }

    const data = await response.json();

    if (
      data?.status !== "success" ||
      !Array.isArray(data.data)
    ) {
      throw new Error(
        data?.message || "Failed to fetch budget sections."
      );
    }

    setBannerSections(data.data);
    return data.data;
  };

  // Initial load: show an error only if a request really fails.
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      const results = await Promise.allSettled([
        fetchSubCategories(),
        fetchBannerSections(),
      ]);

      if (!isMounted) return;

      const errors = results
        .filter((result) => result.status === "rejected")
        .map((result) =>
          getErrorMessage(result.reason, "Failed to load data.")
        );

      setError(errors.length > 0 ? errors.join(" ") : null);
      setLoading(false);
    };

    fetchData();

    return () => {
      isMounted = false;
    };
    // Initial fetch only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage("");

    if (adding) return;

    if (bannerSections.length >= MAX_SECTIONS) {
      setError(`Maximum ${MAX_SECTIONS} items are allowed.`);
      return;
    }

    if (!subId) {
      setError("Please select a subcategory.");
      return;
    }

    if (!tag.trim()) {
      setError("Tag is required.");
      return;
    }

    if (!label.trim()) {
      setError("Label is required.");
      return;
    }

    if (!price.trim()) {
      setError("Price is required.");
      return;
    }

    setAdding(true);

    try {
      const formData = new FormData();
      formData.append("sub_id", subId);
      formData.append("tag", tag.trim());
      formData.append("label", label);
      formData.append("price", price.trim());
      formData.append("badge", badge);

      const response = await fetch(API.add, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to add budget section (HTTP ${response.status}).`
        );
      }

      const data = await response.json();

      if (data?.status !== "success") {
        throw new Error(
          data?.message || "Failed to add budget section."
        );
      }

      // The add succeeded; show success independently of refreshing.
      setSuccessMessage("Budget section added successfully!");

      setSubId("");
      setTag("");
      setLabel("");
      setPrice("");
      setBadge("");

      try {
        await fetchBannerSections();
        setError(null);
      } catch (refreshError) {
        setError(
          "The section was added, but the list could not refresh. Please reload the page."
        );
      }
    } catch (err) {
      setError(
        getErrorMessage(err, "Failed to add budget section.")
      );
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    if (deletingId !== null || adding) return;

    setDeletingId(id);
    setError(null);
    setSuccessMessage("");

    try {
      const response = await fetch(API.delete, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id }),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to delete section (HTTP ${response.status}).`
        );
      }

      const data = await response.json();

      if (data?.status !== "success") {
        throw new Error(
          data?.message || "Failed to delete budget section."
        );
      }

      setBannerSections((previous) =>
        previous.filter((section) => String(section.id) !== String(id))
      );

      setSuccessMessage("Budget section removed successfully!");
    } catch (err) {
      setError(
        getErrorMessage(err, "Failed to delete budget section.")
      );
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <Header />

      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                {successMessage && (
                  <div
                    className="alert alert-success alert-dismissible fade show"
                    role="alert"
                  >
                    {successMessage}
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={() => setSuccessMessage("")}
                    />
                  </div>
                )}

                {error && (
                  <div
                    className="alert alert-danger alert-dismissible fade show"
                    role="alert"
                  >
                    {error}
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={() => setError(null)}
                    />
                  </div>
                )}

                <div className="mb-3">
                  <h5
                    className="mb-0 fw-bold d-inline-block px-3 py-2 rounded"
                    style={{
                      fontSize: "30px",
                      background:
                        "linear-gradient(135deg, #0d6efd15, #0d6efd08)",
                      borderLeft: "5px solid #0d6efd",
                      color: "#0d6efd",
                      letterSpacing: "0.3px",
                    }}
                  >
                    Add Budget Return Gift
                  </h5>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="form-body">
                    <div className="row">
                      <div className="col-md-12">
                        <div className="mb-3">
                          <label htmlFor="subCategory" className="form-label">
                            Sub Category
                          </label>

                          <select
                            id="subCategory"
                            className="form-control"
                            name="sub_category"
                            value={subId}
                            onChange={(e) => setSubId(e.target.value)}
                            disabled={loading || adding}
                          >
                            <option value="">
                              -- Select Sub Category --
                            </option>

                            {subcategory?.map((sub) => (
                              <option
                                key={sub.sub_id}
                                value={sub.sub_id}
                              >
                                {sub.sub_cate}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="col-md-3">
                        <div className="mb-3">
                          <label htmlFor="tag" className="form-label">
                            Tag
                          </label>
                          <input
                            id="tag"
                            type="text"
                            className="form-control"
                            placeholder="Enter tag"
                            value={tag}
                            onChange={(e) => setTag(e.target.value)}
                            disabled={adding}
                          />
                        </div>
                      </div>

                      <div className="col-md-3">
                        <div className="mb-3">
                          <label htmlFor="label" className="form-label">
                            Label
                          </label>
                          <select
                            id="label"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            className="form-control"
                            disabled={adding}
                          >
                            <option value="">Select Label</option>
                            <option value="Under">Under</option>
                            <option value="Above">Above</option>
                          </select>
                        </div>
                      </div>

                      <div className="col-md-3">
                        <div className="mb-3">
                          <label htmlFor="price" className="form-label">
                            Price
                          </label>
                          <input
                            id="price"
                            type="text"
                            className="form-control"
                            placeholder="Enter price range"
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                            disabled={adding}
                          />
                        </div>
                      </div>

                      <div className="col-md-3">
                        <div className="mb-3">
                          <label htmlFor="badge" className="form-label">
                            Badge
                          </label>
                          <select
                            id="badge"
                            value={badge}
                            onChange={(e) => setBadge(e.target.value)}
                            className="form-control"
                            disabled={adding}
                          >
                            <option value="">Select Badge</option>
                            <option value="Hot Deal">Hot Deal</option>
                            <option value="Trending">Trending</option>
                            <option value="Premium">Premium</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-2">
                        <div className="form-actions">
                          <button
                            type="submit"
                            className="btn btn-light-primary text-primary font-medium mb-3"
                            disabled={adding || loading}
                          >
                            {adding ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                />
                                Adding…
                              </>
                            ) : (
                              <>
                                <i className="ti ti-plus me-1" /> Add
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>

                <hr />

                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                  <h5 className="mb-0">Budget Sections List</h5>

                  <span className="badge bg-dark fs-6">
                    {bannerSections.length}{" "}
                    {bannerSections.length === 1 ? "Section" : "Sections"}
                  </span>
                </div>

                <div
                  className="table-responsive"
                  style={{ overflowX: "auto" }}
                >
                  <table className="table border table-striped table-bordered align-middle">
                    <thead>
                      <tr>
                        <th>S.No</th>
                        <th>Sub Category</th>
                        <th>Tag</th>
                        <th>Label</th>
                        <th>Price</th>
                        <th>Badge</th>
                        <th className="text-center">Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            />
                            Loading Budget sections…
                          </td>
                        </tr>
                      ) : bannerSections?.length === 0 ? (
                        <tr>
                          <td
                            colSpan="7"
                            className="text-center fw-bold py-3"
                          >
                            No Budget sections added yet.
                          </td>
                        </tr>
                      ) : (
                        bannerSections?.map((section, index) => {
                          const matchedSubcategory = subcategory.find(
                            (sub) =>
                              String(sub.sub_id) === String(section.sub_id)
                          );

                          return (
                            <tr key={section.id}>
                              <td>{index + 1}</td>

                              <td>
                                <span className="badge bg-light-primary text-primary">
                                  {matchedSubcategory?.sub_cate || "N/A"}
                                </span>
                              </td>

                              <td>
                                <h6 className="mb-0">{section.tag || "—"}</h6>
                              </td>

                              <td>
                                <h6 className="mb-0">{section.label || "—"}</h6>
                              </td>

                              <td>
                                <h6 className="mb-0">{section.price || "—"}</h6>
                              </td>

                              <td>
                                <h6 className="mb-0">{section.badge || "—"}</h6>
                              </td>

                              <td className="text-center">
                                <div className="btn-group">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleDelete(section.id)}
                                    disabled={
                                      deletingId !== null || adding
                                    }
                                    aria-label="Delete budget section"
                                  >
                                    {String(deletingId) ===
                                    String(section.id) ? (
                                      <>
                                        <span
                                          className="spinner-border spinner-border-sm me-1"
                                          role="status"
                                        />
                                        Removing…
                                      </>
                                    ) : (
                                      <i className="ti ti-trash" />
                                    )}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BannerSection;
